import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Bell,
  Clock,
  TrendingUp,
  Users,
  Target,
  Award,
  ChevronRight,
  CheckCircle,
  Settings,
  BarChart3,
  FileText,
  UserPlus,
  Mail,
  Clipboard,
  Activity,
  Zap,
  Timer,
  ArrowUp,
  ArrowDown,
  CheckSquare
} from 'lucide-react';
import dataManager from '../utils/DataManager';
import Weather from './Weather';
import DoerBoard from './DoerBoard';

// Import components for background loading
import DelegationTasks from './DelegationTasks';
import FMSTasks from './FMSTasks';
import HTTasks from './HTTasks';
import PCTasks from './PCTasks';
import HSHelpSlip from './HSHelpSlip';

const Overview = ({ currentUser, onTabChange, boardMode = false }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  // Get cached data directly without useCachedData to avoid infinite loops
  const [delegationData, setDelegationData] = useState([]);
  const [fmsData, setFmsData] = useState([]);
  const [htData, setHtData] = useState([]);
  const [pcData, setPcData] = useState([]);
  const [hsData, setHsData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Background loading state
  const [backgroundLoading, setBackgroundLoading] = useState({
    delegation: false,
    fms: false,
    ht: false,
    pc: false,
    hs: false
  });
  const [loadingProgress, setLoadingProgress] = useState([]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Add loading progress message
  const addLoadingMessage = useCallback((module, message) => {
    setLoadingProgress(prev => [...prev, { module, message, timestamp: Date.now() }]);
  }, []);

  // Background component mounting for data loading
  const [mountComponents, setMountComponents] = useState({
    delegation: false,
    fms: false,
    ht: false,
    pc: false,
    hs: false
  });

  // Start background loading based on user permissions
  useEffect(() => {
    if (!currentUser?.name) return;

   // Replace the sequential for loop in startBackgroundLoading with this concurrent approach:

const startBackgroundLoading = async () => {
  addLoadingMessage('system', 'Starting background data loading...');
  
  // Get list of permitted modules
  const permittedModules = [];
  if (currentUser.permissions.canViewDelegation) permittedModules.push('delegation');
  if (currentUser.permissions.canViewFMS) permittedModules.push('fms');
  if (currentUser.permissions.canViewHT) permittedModules.push('ht');
  if (currentUser.permissions.canViewPC) permittedModules.push('pc');
  if (currentUser.permissions.canViewHS) permittedModules.push('hs');

  // If no modules to load, show overview immediately
  if (permittedModules.length === 0) {
    addLoadingMessage('system', '✓ No modules to load - user has no permissions');
    setLoading(false);
    return;
  }

  // Track completion status for each module
  const moduleCompletionStatus = {};
  permittedModules.forEach(module => {
    moduleCompletionStatus[module] = false;
  });

  // Function to check if all modules are completed
  const checkAllModulesCompleted = () => {
    const allCompleted = permittedModules.every(module => moduleCompletionStatus[module]);
    if (allCompleted) {
      addLoadingMessage('system', '✓ All modules data loaded - updating overview...');
      // Final data load from cache
      loadCachedData();
      // Small delay to ensure UI updates, then show overview
      setTimeout(() => {
        setLoading(false);
        addLoadingMessage('system', '✓ Overview ready!');
      }, 500);
    }
    return allCompleted;
  };

  // CONCURRENT LOADING: Start all modules simultaneously
  const moduleLoadingPromises = permittedModules.map(async (module) => {
    addLoadingMessage(module, `Loading ${module.toUpperCase()} data...`);
    
    setBackgroundLoading(prev => ({ ...prev, [module]: true }));
    
    // Mount the component to trigger data loading
    setMountComponents(prev => ({ ...prev, [module]: true }));
    
    // Wait for component to actually load and cache data
    let attempts = 0;
    const maxAttempts = 15; // 15 attempts * 1 second = 15 seconds max wait
    
    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      attempts++;
      
      // Check if data was actually loaded and cached
      const hasData = dataManager.hasData(module, currentUser.name);
      const isFresh = dataManager.isDataFresh(module, currentUser.name);
      
      if (hasData && isFresh) {
        addLoadingMessage(module, `✓ ${module.toUpperCase()} data loaded successfully`);
        moduleCompletionStatus[module] = true;
        setBackgroundLoading(prev => ({ ...prev, [module]: false }));
        
        // Unmount component after data is loaded to save memory
        setTimeout(() => {
          setMountComponents(prev => ({ ...prev, [module]: false }));
        }, 2000);
        break;
      } else if (attempts === maxAttempts) {
        // Timeout - mark as completed anyway to avoid infinite loading
        addLoadingMessage(module, `⚠ ${module.toUpperCase()} data loading timeout - continuing anyway`);
        moduleCompletionStatus[module] = true;
        setBackgroundLoading(prev => ({ ...prev, [module]: false }));
        setTimeout(() => {
          setMountComponents(prev => ({ ...prev, [module]: false }));
        }, 2000);
      } else {
        addLoadingMessage(module, `⏳ ${module.toUpperCase()} still loading... (${attempts}/${maxAttempts})`);
      }
    }
  });

  // Wait for all modules to complete loading concurrently
  try {
    await Promise.all(moduleLoadingPromises);
    
    // Final check and load cached data
    if (!checkAllModulesCompleted()) {
      addLoadingMessage('system', '⚠ Some modules may still be loading, but showing overview now');
      loadCachedData();
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  } catch (error) {
    console.error('Error during concurrent module loading:', error);
    addLoadingMessage('system', '❌ Error during data loading - showing overview anyway');
    loadCachedData();
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }
};

    startBackgroundLoading();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.name, currentUser?.permissions, addLoadingMessage]);

  // Load cached data on mount and periodically
  const loadCachedData = useCallback(() => {
  if (!currentUser?.name) return;

  const userId = currentUser.name;
  
  // Get data from cache directly with better error handling
  try {
    // Delegation cache also carries recently completed tasks from the archive
    // sheet; Overview counts live work only.
    const delegation = (dataManager.getDataWithFallback('delegation', userId) || [])
      .filter(task => task._source !== 'archive');
    const fms = dataManager.getDataWithFallback('fms', userId) || [];
    const ht = dataManager.getDataWithFallback('ht', userId) || [];
    const pc = dataManager.getDataWithFallback('pc', userId) || [];
    const hs = dataManager.getDataWithFallback('hs', userId) || [];

    setDelegationData(delegation);
    setFmsData(fms);
    setHtData(ht);
    setPcData(pc);
    setHsData(hs);

    console.log('Overview data refreshed:', {
      delegation: delegation.length,
      fms: fms.length,
      ht: ht.length,
      pc: pc.length,
      hs: hs.length,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error loading cached data:', error);
    // Set empty arrays as fallback
    setDelegationData([]);
    setFmsData([]);
    setHtData([]);
    setPcData([]);
    setHsData([]);
  }
}, [currentUser?.name]);


  // ✅ REPLACE with cache update listener:
useEffect(() => {
  if (!loading) {
    // Listen for cache updates more frequently (every 30 seconds)
    const interval = setInterval(loadCachedData, 30000); // 30 seconds
    return () => clearInterval(interval);
  }
}, [loading, loadCachedData]);

  // Calculate dynamic stats based on real data
  const stats = useMemo(() => {
    if (loading) return [];
    
    const baseStats = [];
    
    if (currentUser.permissions.canViewHT) {
      const htRaisedOnYou = htData.filter(task => task.issueDelegatedTo === currentUser.name);
      const htReplyPending = htRaisedOnYou.filter(task => 
        (!task.replyActual || task.replyActual.trim() === '')
      );
      
      baseStats.push({
        title: 'HT Reply Pending', 
        value: htReplyPending.length.toString(), 
        change: htReplyPending.length > 0 ? `${htReplyPending.length} awaiting` : 'All clear', 
        trend: htReplyPending.length > 3 ? 'up' : 'stable', 
        icon: Mail, 
        color: 'text-red-600 bg-red-50',
        description: 'Help tickets awaiting your response',
        onClick: () => onTabChange('ht-tasks'),
        total: htData.length
      });
    }
    
    if (currentUser.permissions.canViewDelegation) {
      const pendingTasks = delegationData.filter(task => 
        (task.delegation_status || 'pending').toLowerCase().includes('pending')
      );
      
      baseStats.push({
        title: 'Delegation Tasks', 
        value: pendingTasks.length.toString(), 
        change: delegationData.length > 0 ? `${delegationData.length} total` : 'No tasks', 
        trend: pendingTasks.length > 0 ? 'up' : 'stable', 
        icon: Users, 
        color: 'text-blue-600 bg-blue-50',
        description: 'Tasks assigned to you',
        onClick: () => onTabChange('delegation'),
        total: delegationData.length
      });
    }
    
    if (currentUser.permissions.canViewFMS) {
      
   const userFMSData = fmsData.filter(task => {
  const taskDoer = task.doer || task.assignedTo || '';
  const fmsType = task.fms || '';
  const isChecklistOrHelpTicket = fmsType.toLowerCase().includes('checklist task') || 
                                  fmsType.toLowerCase().includes('helpticket');
  return taskDoer === currentUser.name && !isChecklistOrHelpTicket;
});
const totalFMSTasks = userFMSData.length;

const delayedTasks = userFMSData.filter(task => task.delay && task.delay.trim() !== '').length;

      baseStats.push({
        title: 'FMS', 
        value: totalFMSTasks.toString(), 
        change: delayedTasks > 0 ? `${delayedTasks} delayed` : 'On track', 
        trend: delayedTasks > 0 ? 'down' : 'up', 
        icon: FileText, 
        color: 'text-green-600 bg-green-50',
        description: 'Flow Management System tasks',
        onClick: () => onTabChange('fms'),
        total: totalFMSTasks
      });
    }
    
    if (currentUser.permissions.canViewPC) {
      const overdueTasks = pcData.filter(task => {
        const delay = task.delay || '';
        return delay && delay.trim() !== '';
      }).length;
      
      baseStats.push({
        title: 'PC Dashboard', 
        value: pcData.length.toString(), 
        change: overdueTasks > 0 ? `${overdueTasks} overdue` : 'On schedule', 
        trend: overdueTasks > 0 ? 'down' : 'up', 
        icon: Clipboard, 
        color: 'text-purple-600 bg-purple-50',
        description: 'Process Coordinator tasks',
        onClick: () => onTabChange('pc'),
        total: pcData.length
      });
    }
    
    if (currentUser.permissions.canViewHS) {
  // Check if user is director
  const isDirector = 
    (currentUser?.role?.toLowerCase() === 'director') || 
    (currentUser?.department?.toLowerCase() === 'director');
  
  let replyPendingHS, totalHS;
  
  if (isDirector) {
    // Directors: show all help slips awaiting director's reply
    const directorTasks = hsData.filter(task => 
      task.helpSlipId && task.helpSlipId.trim() !== ''
    );
    replyPendingHS = directorTasks.filter(task => 
      (!task.replyActual || task.replyActual.trim() === '')
    ).length;
    totalHS = directorTasks.length;
  } else {
    // Regular users: show only their own tasks
    const userHSData = hsData.filter(task => 
      task.name === currentUser.name || task.assignedTo === currentUser.name
    );
    replyPendingHS = userHSData.filter(task => 
      task.replyPlanned && task.replyPlanned.trim() !== '' &&
      (!task.replyActual || task.replyActual.trim() === '')
    ).length;
    totalHS = userHSData.length;
  }
  
  baseStats.push({
    title: 'Help Slips', 
    value: replyPendingHS.toString(), 
    change: totalHS > 0 ? `${totalHS} total` : 'No slips', 
    trend: replyPendingHS > 0 ? 'up' : 'stable', 
    icon: UserPlus, 
    color: 'text-orange-600 bg-orange-50',
    description: isDirector 
      ? 'Help slips awaiting your reply as director'
      : 'Help slips requiring attention',
    onClick: () => onTabChange('hs'),
    total: totalHS
  });
}
    
    // Checklist Tasks calculation
if (currentUser.permissions.canViewFMS) {
  const userChecklistTasks = fmsData.filter(task => {
    const taskDoer = task.doer || task.assignedTo || '';
    const fmsType = task.fms || '';
    const isChecklistTask = fmsType.toLowerCase().includes('checklist task');
    const isUserDoer = taskDoer === currentUser.name;
    
    return isChecklistTask && isUserDoer;
  });
  
  const overdueChecklist = userChecklistTasks.filter(task => {
    const delay = parseFloat(task.delay || 0);
    return delay > 0;
  }).length;
  
  baseStats.push({
    title: 'Checklist Tasks', 
    value: userChecklistTasks.length.toString(), 
    change: overdueChecklist > 0 ? `${overdueChecklist} overdue` : 'All on track', 
    trend: overdueChecklist > 0 ? 'down' : 'up', 
    icon: CheckSquare, 
    color: 'text-indigo-600 bg-indigo-50',
    description: 'Checklist tasks assigned to you',
    onClick: () => onTabChange('checklist'),
    total: userChecklistTasks.length
  });
}
    return baseStats;
  }, [delegationData, fmsData, htData, pcData, hsData, currentUser.permissions, onTabChange, currentUser.name, currentUser.role, currentUser.department, loading]);

  // Get priority tasks from all modules - FILTERED BY CURRENT USER
  const priorityTasks = useMemo(() => {
    const tasks = [];
    
    // Add HT urgent tasks - only those assigned to current user
    if (currentUser.permissions.canViewHT) {
      const urgentHT = htData
        .filter(task => {
          // Filter by user: either assigned to them OR raised by them
          const isAssignedToUser = task.issueDelegatedTo === currentUser.name;
          const isRaisedByUser = task.name === currentUser.name;
          const isHighPriority = task.challengeLevel?.toUpperCase() === 'HIGH';
          const isPending = (!task.replyActual || task.replyActual.trim() === '');

          return (isAssignedToUser || isRaisedByUser) && isHighPriority && isPending;
        })
        .slice(0, 2)
        .map(task => ({
          id: task.ticketId || task.id,
          title: task.challengeIssue || 'HT Task',
          type: 'HT',
          priority: 'urgent',
          dueDate: task.replyPlanned || task.timestamp,
          module: 'ht-tasks',
          assignedTo: task.issueDelegatedTo,
          raisedBy: task.name
        }));
      tasks.push(...urgentHT);
    }
    
    // Add high priority delegation tasks - only those assigned to current user
    if (currentUser.permissions.canViewDelegation) {
      const urgentDelegation = delegationData
        .filter(task => {
          // Filter by doer name (person task is assigned to)
          const isAssignedToUser = (task.doer_name || task.doer) === currentUser.name;
          const score = parseFloat(task['80_20'] || task.priority_score || 0);
          const isHighPriority = score >= 75;
          const isPending = (task.delegation_status || 'pending').toLowerCase().includes('pending');

          
          return isAssignedToUser && isHighPriority && isPending;
        })
        .slice(0, 2)
        .map(task => ({
          id: task.id,
          title: task.task || 'Delegation Task',
          type: 'Delegation',
          priority: 'high',
          dueDate: task.final_date || task.first_date,
          module: 'delegation',
          assignedTo: task.doer_name || task.doer
        }));
      tasks.push(...urgentDelegation);
    }
    
    
    // Add overdue FMS tasks - only those assigned to current user (exclude CheckList Task and HelpTicket)
if (currentUser.permissions.canViewFMS) {
  const overdueFMS = fmsData
    .filter(task => {
      // Filter by doer (person task is assigned to)
      const isAssignedToUser = task.doer === currentUser.name;
      const isOverdue = task.delay && task.delay.trim() !== '';
      
      // Exclude CheckList Task and HelpTicket items
      const fmsType = task.fms || '';
      const isChecklistOrHelpTicket = fmsType.toLowerCase().includes('checklist task') || 
                                      fmsType.toLowerCase().includes('helpticket');
      
      return isAssignedToUser && isOverdue && !isChecklistOrHelpTicket;
    })
    .slice(0, 2)
    .map(task => ({
      id: task.id,
      title: task.what_to_do || 'FMS Task',
      type: 'FMS',
      priority: 'overdue',
      dueDate: task.planned || 'Not set',
      module: 'fms',
      assignedTo: task.doer
    }));
  tasks.push(...overdueFMS);
}

    // Add overdue PC tasks - only those where current user is PC
    if (currentUser.permissions.canViewPC) {
      const overduePC = pcData
        .filter(task => {
          // Filter by PC field (Process Coordinator)
          const isPCUser = (task.pc || task.pcdeo) === currentUser.name;
          const isOverdue = task.delay && task.delay.trim() !== '';
          
          return isPCUser && isOverdue;
        })
        .slice(0, 2)
        .map(task => ({
          id: task.id,
          title: task.what_to_do || 'PC Task',
          type: 'PC',
          priority: 'overdue',
          dueDate: task.planned || 'Not set',
          module: 'pc',
          assignedTo: task.doer,
          pc: task.pc || task.pcdeo
        }));
      tasks.push(...overduePC);
    }

    // Add HS tasks - only those where current user is involved
    if (currentUser.permissions.canViewHS) {
      const urgentHS = hsData
        .filter(task => {
          // Filter by user involvement: either raised by them or assigned to them
          const isRaisedByUser = task.name === currentUser.name;
          const isAssignedToUser = task.assignedTo === currentUser.name;
          const isReplyPending = task.replyPlanned && task.replyPlanned.trim() !== '' && 
                                (!task.replyActual || task.replyActual.trim() === '');
          
          return (isRaisedByUser || isAssignedToUser) && isReplyPending;
        })
        .slice(0, 2)
        .map(task => ({
          id: task.helpSlipId || task.id,
          title: task.challengeIssue || 'Help Slip',
          type: 'HS',
          priority: 'pending',
          dueDate: task.replyPlanned || 'Not set',
          module: 'hs',
          raisedBy: task.name
        }));
      tasks.push(...urgentHS);
    }
    
    return tasks.slice(0, 6); // Limit to 6 tasks
  }, [delegationData, fmsData, htData, pcData, hsData, currentUser.permissions, currentUser.name]);

  // Generate recent activities from real cached data - FILTERED BY CURRENT USER
  const activities = useMemo(() => {
    const allActivities = [];
    
    // Helper function to format time
    // ✅ FIXED VERSION:
const formatActivityTime = (dateStr) => {
  if (!dateStr || dateStr === 'Recently') return 'Recently';
  
  try {
    const date = new Date(dateStr);
    // ✅ ADD: Check if date is valid before calculating
    if (isNaN(date.getTime())) return 'Recently';
    
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    // ✅ ADD: Check for negative values (invalid dates)
    if (diffInHours < 0) return 'Recently';
    
    if (diffInHours < 1) {
      const minutes = Math.floor(diffInHours * 60);
      return minutes <= 0 ? 'Just now' : `${minutes}m ago`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInHours / 24);
      return `${days}d ago`;
    }
  } catch (error) {
    return 'Recently';
  }
};

    // Helper function to get timestamp for sorting
    const getTimestampForSorting = (dateStr) => {
      if (!dateStr || dateStr === 'Recently') return 0;
      try {
        const date = new Date(dateStr);
        return isNaN(date.getTime()) ? 0 : date.getTime();
      } catch (error) {
        return 0;
      }
    };

    // Recent delegation tasks - only those assigned to current user
    // Sort by Task Created Date
    if (currentUser.permissions.canViewDelegation && delegationData.length > 0) {
      const userDelegationTasks = delegationData.filter(task => 
        (task.doer_name || task.doer) === currentUser.name
      );
      
      // Sort by task_created_date and take most recent
      const recentDelegation = [...userDelegationTasks]
        .sort((a, b) => {
          const dateA = getTimestampForSorting(a.task_created_date || a.first_date);
          const dateB = getTimestampForSorting(b.task_created_date || b.first_date);
          return dateB - dateA; // Latest first
        })
        .slice(0, 3);

      recentDelegation.forEach(task => {
        const taskTitle = task.task || 'Delegation task';
        const truncatedTitle = taskTitle.length > 50 ? taskTitle.substring(0, 50) + '...' : taskTitle;
        
        // Use task_created_date, fallback to first_date, then to 'Recently'
        let activityDate = task.task_created_date || task.first_date;
        
        // Handle various date formats for delegation tasks
        if (activityDate) {
          // Check if it's already a valid date string
          const testDate = new Date(activityDate);
          if (isNaN(testDate.getTime())) {
            // Try common date formats
            const formats = [
              // Try MM/DD/YYYY format
              activityDate.replace(/(\d{1,2})\/(\d{1,2})\/(\d{4})/, '$3-$1-$2'),
              // Try DD/MM/YYYY format  
              activityDate.replace(/(\d{1,2})\/(\d{1,2})\/(\d{4})/, '$3-$2-$1'),
              // Try MM-DD-YYYY format
              activityDate.replace(/(\d{1,2})-(\d{1,2})-(\d{4})/, '$3-$1-$2'),
              // Try DD-MM-YYYY format
              activityDate.replace(/(\d{1,2})-(\d{1,2})-(\d{4})/, '$3-$2-$1')
            ];
            
            // Find first valid format
            for (const format of formats) {
              const formatTest = new Date(format);
              if (!isNaN(formatTest.getTime())) {
                activityDate = format;
                break;
              }
            }
            
            // If still invalid, use 'Recently'
            const finalTest = new Date(activityDate);
            if (isNaN(finalTest.getTime())) {
              activityDate = 'Recently';
            }
          }
        } else {
          activityDate = 'Recently';
        }
        
        allActivities.push({
          id: `del-${task.id || task.rowNumber}`,
          action: 'Delegation task assigned to you',
          details: truncatedTitle,
          time: formatActivityTime(activityDate),
          timestamp: getTimestampForSorting(activityDate),
          icon: Users,
          color: 'text-blue-600',
          module: 'delegation'
        });
      });
    }
    
    // Recent FMS activities - only those assigned to current user
    // Sort by Planned date
    if (currentUser.permissions.canViewFMS && fmsData.length > 0) {
      const userFMSTasks = fmsData.filter(task => task.doer === currentUser.name);
      
      const recentFMS = [...userFMSTasks]
        .sort((a, b) => {
          const dateA = getTimestampForSorting(a.planned);
          const dateB = getTimestampForSorting(b.planned);
          return dateB - dateA; // Latest first
        })
        .slice(0, 3);

      recentFMS.forEach(task => {
        const fmsType = task.fms || 'FMS task';
        const whatToDo = task.what_to_do || '';
        const details = whatToDo ? (whatToDo.length > 50 ? whatToDo.substring(0, 50) + '...' : whatToDo) : fmsType;
        const plannedDate = task.planned;
        
        allActivities.push({
          id: `fms-${task.id || task.rowNumber}`,
          action: task.delay && task.delay.trim() ? 'Your FMS task is delayed' : 'FMS task assigned to you',
          details: details,
          time: formatActivityTime(plannedDate),
          timestamp: getTimestampForSorting(plannedDate),
          icon: FileText,
          color: task.delay && task.delay.trim() ? 'text-orange-600' : 'text-green-600',
          module: 'fms'
        });
      });
    }
    
    // Recent HT activities - only those involving current user
    // Sort by Timestamp
    if (currentUser.permissions.canViewHT && htData.length > 0) {
      const userHTTasks = htData.filter(task => 
        task.issueDelegatedTo === currentUser.name || task.name === currentUser.name
      );
      
      const recentHT = [...userHTTasks]
        .sort((a, b) => {
          const dateA = getTimestampForSorting(a.timestamp);
          const dateB = getTimestampForSorting(b.timestamp);
          return dateB - dateA; // Latest first
        })
        .slice(0, 3);

      recentHT.forEach(task => {
        const issue = task.challengeIssue || 'Help ticket';
        const truncatedIssue = issue.length > 50 ? issue.substring(0, 50) + '...' : issue;
        const timestamp = task.timestamp;
        
        // Determine action based on user role and task status
        let action = 'Help ticket received';
        if (task.issueDelegatedTo === currentUser.name) {
          if (task.replyActual && task.replyActual.trim() !== '') {
            action = 'You replied to help ticket';
          } else if (task.replyPlanned && task.replyPlanned.trim() !== '') {
            action = 'Help ticket assigned to you';
          } else {
            action = 'Help ticket assigned to you';
          }
        } else if (task.name === currentUser.name) {
          if (task.replyActual && task.replyActual.trim() !== '') {
            action = 'Your help ticket was replied';
          } else {
            action = 'You raised a help ticket';
          }
        }
        
        allActivities.push({
          id: `ht-${task.ticketId || task.id}`,
          action: action,
          details: truncatedIssue,
          time: formatActivityTime(timestamp),
          timestamp: getTimestampForSorting(timestamp),
          icon: Mail,
          color: task.challengeLevel?.toUpperCase() === 'HIGH' ? 'text-red-600' : 'text-blue-600',
          module: 'ht-tasks'
        });
      });
    }

    // Recent PC activities - only those where current user is PC
    // Sort by Planned date
    if (currentUser.permissions.canViewPC && pcData.length > 0) {
      const userPCTasks = pcData.filter(task => 
        (task.pc || task.pcdeo) === currentUser.name
      );
      
      const recentPC = [...userPCTasks]
        .sort((a, b) => {
          const dateA = getTimestampForSorting(a.planned);
          const dateB = getTimestampForSorting(b.planned);
          return dateB - dateA; // Latest first
        })
        .slice(0, 3);

      recentPC.forEach(task => {
        const whatToDo = task.what_to_do || 'PC task';
        const truncatedTask = whatToDo.length > 50 ? whatToDo.substring(0, 50) + '...' : whatToDo;
        const plannedDate = task.planned;
        
        allActivities.push({
          id: `pc-${task.id || task.rowNumber}`,
          action: task.delay && task.delay.trim() ? 'PC task under your supervision is overdue' : 'PC task under your supervision',
          details: truncatedTask,
          time: formatActivityTime(plannedDate),
          timestamp: getTimestampForSorting(plannedDate),
          icon: Clipboard,
          color: task.delay && task.delay.trim() ? 'text-red-600' : 'text-purple-600',
          module: 'pc'
        });
      });
    }

    // Recent HS activities - only those involving current user
    // Sort by Timestamp
    if (currentUser.permissions.canViewHS && hsData.length > 0) {
      const userHSTasks = hsData.filter(task => 
        task.name === currentUser.name || task.assignedTo === currentUser.name
      );
      
      const recentHS = [...userHSTasks]
        .sort((a, b) => {
          const dateA = getTimestampForSorting(a.timestamp);
          const dateB = getTimestampForSorting(b.timestamp);
          return dateB - dateA; // Latest first
        })
        .slice(0, 3);

      recentHS.forEach(task => {
        const issue = task.challengeIssue || 'Help slip';
        const truncatedIssue = issue.length > 50 ? issue.substring(0, 50) + '...' : issue;
        const timestamp = task.timestamp;
        
        let action = 'Help slip created';
        if (task.name === currentUser.name) {
          if (task.replyActual && task.replyActual.trim() !== '') {
            action = 'Your help slip was replied';
          } else {
            action = 'You created a help slip';
          }
        } else if (task.assignedTo === currentUser.name) {
          if (task.replyActual && task.replyActual.trim() !== '') {
            action = 'You replied to help slip';
          } else {
            action = 'Help slip assigned to you';
          }
        }
        
        allActivities.push({
          id: `hs-${task.helpSlipId || task.id}`,
          action: action,
          details: truncatedIssue,
          time: formatActivityTime(timestamp),
          timestamp: getTimestampForSorting(timestamp),
          icon: UserPlus,
          color: 'text-orange-600',
          module: 'hs'
        });
      });
    }
    
    // Sort ALL activities by timestamp (latest first) and limit to 8
    return allActivities
      .sort((a, b) => {
        // Sort by actual timestamp for proper chronological order
        return b.timestamp - a.timestamp; // Latest first
      })
      .slice(0, 8);
  }, [delegationData, fmsData, htData, pcData, hsData, currentUser.permissions, currentUser.name]);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <ArrowUp className="w-4 h-4 text-green-500" />;
      case 'down': return <ArrowDown className="w-4 h-4 text-red-500" />;
      default: return <TrendingUp className="w-4 h-4 text-blue-500" />;
    }
  };

  const greeting = currentTime.getHours() < 12 ? 'Good Morning' : currentTime.getHours() < 18 ? 'Good Afternoon' : 'Good Evening';

  // Module loading status row helper
  const renderModuleStatus = (key, label, Icon) => {
    const isLoading = backgroundLoading[key];
    const isReady = dataManager.hasData(key, currentUser.name);
    return (
      <div key={key} style={{
        display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px',
        borderRadius: '8px', border: `1px solid ${isReady ? 'var(--status-success)' : isLoading ? 'var(--gold-500,#c8a03c)' : 'var(--border)'}`,
        background: isReady ? 'rgba(34,197,94,0.06)' : isLoading ? 'rgba(200,160,60,0.06)' : 'var(--surface-2)',
        transition: 'all 0.3s'
      }}>
        <Icon size={16} style={{ color: isReady ? 'var(--status-success)' : isLoading ? 'var(--gold-500,#c8a03c)' : 'var(--text-muted)', flexShrink: 0 }} className={isLoading ? 'animate-pulse' : ''} />
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', margin: 0 }}>{label}</p>
          <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
            {isLoading ? 'Loading...' : isReady ? 'Ready' : 'Waiting'}
          </p>
        </div>
        {isReady && <CheckCircle size={14} style={{ color: 'var(--status-success)', flexShrink: 0 }} />}
      </div>
    );
  };

  // Show loading state if still loading
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Hero Banner (the doer board has no hero) */}
        {!boardMode && <div className="k-hero">
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', position: 'relative', zIndex: 1 }}>
            <div>
              <h1 style={{ fontSize: 'clamp(22px,4vw,32px)', fontWeight: 800, color: '#fff', marginBottom: '6px' }}>
                {greeting}, {currentUser.name.split(' ')[0]}!
              </h1>
              <p style={{ fontSize: '14px', opacity: 0.8, color: '#fff', marginBottom: '8px' }}>{formatDate(currentTime)}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', opacity: 0.75, color: '#fff', flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Clock size={13} /> <span style={{ fontFamily: 'monospace' }}>{formatTime(currentTime)}</span>
                </span>
                <Weather />
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end', marginBottom: '8px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#fbbf24', animation: 'pulse 1.5s infinite' }}></div>
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>Loading Data...</span>
              </div>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', margin: '0 0 2px' }}>{currentUser.role} · {currentUser.department}</p>
              <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', margin: 0 }}>{currentUser.email}</p>
            </div>
          </div>
          <div className="k-gold-line" style={{ marginTop: '20px' }}></div>
        </div>}

        {/* Loading card */}
        <div className="k-card">
          <div className="k-loading" style={{ paddingTop: '32px', paddingBottom: '8px' }}>
            <div className="k-loading-spinner"></div>
            <p className="k-loading-text">Initialising modules &amp; loading your data...</p>
          </div>

          <div style={{ padding: '0 24px 24px', maxWidth: '640px', margin: '0 auto' }}>
            {/* Module status grid */}
            <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>Module Status</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: '8px', marginBottom: '20px' }}>
              {currentUser.permissions.canViewDelegation && renderModuleStatus('delegation', 'Delegation Tasks', Users)}
              {currentUser.permissions.canViewFMS && renderModuleStatus('fms', 'FMS Tasks', FileText)}
              {currentUser.permissions.canViewHT && renderModuleStatus('ht', 'Help Tickets', Mail)}
              {currentUser.permissions.canViewPC && renderModuleStatus('pc', 'PC Dashboard', Clipboard)}
              {currentUser.permissions.canViewHS && renderModuleStatus('hs', 'Help Slips', UserPlus)}
            </div>

            {/* Loading log */}
            {loadingProgress.length > 0 && (
              <div style={{ background: 'var(--surface-2)', borderRadius: '8px', padding: '12px 14px', maxHeight: '160px', overflowY: 'auto', border: '1px solid var(--border)' }}>
                <p style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Loading Log</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  {loadingProgress.map((log, i) => (
                    <div key={i} style={{ display: 'flex', gap: '8px', fontSize: '11px' }}>
                      <span style={{ color: 'var(--text-muted)', flexShrink: 0, fontFamily: 'monospace' }}>{new Date(log.timestamp).toLocaleTimeString()}</span>
                      <span style={{ color: log.message.includes('✓') ? 'var(--status-success)' : log.message.includes('⚠') ? 'var(--status-warning)' : 'var(--text-secondary)' }}>{log.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Background Component Mounting (Hidden) */}
        <div style={{ display: 'none' }}>
          {mountComponents.delegation && currentUser.permissions.canViewDelegation && <DelegationTasks currentUser={currentUser} />}
          {mountComponents.fms && currentUser.permissions.canViewFMS && <FMSTasks currentUser={currentUser} />}
          {mountComponents.ht && currentUser.permissions.canViewHT && <HTTasks currentUser={currentUser} />}
          {mountComponents.pc && currentUser.permissions.canViewPC && <PCTasks currentUser={currentUser} />}
          {mountComponents.hs && currentUser.permissions.canViewHS && <HSHelpSlip currentUser={currentUser} />}
        </div>
      </div>
    );
  }

  // Stat bar color
  const statBarColor = (color) => {
    if (color.includes('red')) return 'var(--status-error)';
    if (color.includes('blue')) return 'var(--forest-500,#22553a)';
    if (color.includes('green')) return 'var(--status-success)';
    if (color.includes('purple')) return '#8b5cf6';
    if (color.includes('orange')) return 'var(--status-warning)';
    return 'var(--gold-500,#c8a03c)';
  };

  // Urgency-aware card styling
  const getCardUrgency = (stat) => {
    const val = parseInt(stat.value) || 0;
    if (val === 0) return 'clear';
    if (stat.trend === 'down') return 'critical';
    return 'active';
  };

  const cardStyles = {
    critical: {
      border: '1.5px solid rgba(220,38,38,0.35)',
      background: 'rgba(220,38,38,0.03)',
      barColor: 'var(--status-error)',
      changeColor: 'var(--status-error)',
      dotColor: '#ef4444',
    },
    active: {
      border: '1px solid var(--border)',
      background: 'var(--bg-surface)',
      barColor: null, // use statBarColor
      changeColor: 'var(--status-success)',
      dotColor: null,
    },
    clear: {
      border: '1px solid var(--border)',
      background: 'var(--bg-surface)',
      barColor: 'var(--border)',
      changeColor: 'var(--text-muted)',
      dotColor: null,
      opacity: 0.5,
    },
  };

  // Doer-board layout: the board is the whole page, no hero or cards.
  if (boardMode) {
    return (
      <DoerBoard
        currentUser={currentUser}
        htData={htData}
        delegationData={delegationData}
        fmsData={fmsData}
        loaded={{
          ht: dataManager.hasData('ht', currentUser.name),
          delegation: dataManager.hasData('delegation', currentUser.name),
          fms: dataManager.hasData('fms', currentUser.name),
        }}
        onTabChange={onTabChange}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* Hero Banner */}
      <div className="k-hero">
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', position: 'relative', zIndex: 1 }}>
          <div>
            <h1 style={{ fontSize: 'clamp(22px,4vw,32px)', fontWeight: 800, color: '#fff', marginBottom: '6px' }}>
              {greeting}, {currentUser.name.split(' ')[0]}!
            </h1>
            <p style={{ fontSize: '14px', opacity: 0.82, color: '#fff', marginBottom: '8px' }}>{formatDate(currentTime)}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', opacity: 0.72, color: '#fff', flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Clock size={13} /> <span style={{ fontFamily: 'monospace' }}>{formatTime(currentTime)}</span>
              </span>
              <Weather />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <span style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: 'rgba(74,222,128,0.15)', border: '1px solid rgba(74,222,128,0.3)',
                borderRadius: 99, padding: '3px 10px', fontSize: 11, color: 'rgba(255,255,255,0.9)',
              }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', animation: 'pulse 2s infinite' }} />
                System Online
              </span>
              <span style={{
                background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: 99, padding: '3px 10px', fontSize: 11, color: 'rgba(255,255,255,0.85)',
              }}>
                {currentUser.role} · {currentUser.department}
              </span>
            </div>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{currentUser.email}</span>
          </div>
        </div>
        <div className="k-gold-line" style={{ marginTop: '20px' }}></div>
      </div>

      {/* Stats Grid */}
      {stats.length > 0 && (
        <div className="k-stats-grid" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(210px, 1fr))` }}>
          {stats.map((stat, i) => {
            const urgency = getCardUrgency(stat);
            const cs = cardStyles[urgency];
            const barColor = cs.barColor || statBarColor(stat.color);
            return (
              <div
                key={i}
                onClick={stat.onClick}
                style={{
                  cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0,
                  border: cs.border, background: cs.background,
                  borderRadius: 14, padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                  opacity: cs.opacity || 1,
                  transition: 'transform 0.15s, box-shadow 0.15s, opacity 0.15s',
                  position: 'relative', overflow: 'hidden',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)'; }}
              >
                {/* Critical urgency accent bar at top */}
                {urgency === 'critical' && (
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, borderRadius: '14px 14px 0 0', background: 'var(--status-error)' }} />
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, marginTop: urgency === 'critical' ? 4 : 0 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: urgency === 'critical' ? 'var(--status-error)' : urgency === 'clear' ? 'var(--text-muted)' : statBarColor(stat.color),
                    background: urgency === 'critical' ? 'rgba(220,38,38,0.1)' : urgency === 'clear' ? 'var(--border)' : `${statBarColor(stat.color)}18`,
                  }}>
                    <stat.icon size={18} />
                  </div>
                  {urgency === 'clear'
                    ? <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 500 }}>All clear</span>
                    : getTrendIcon(stat.trend)
                  }
                </div>
                <p style={{ fontSize: 26, fontWeight: 800, color: urgency === 'critical' ? 'var(--status-error)' : urgency === 'clear' ? 'var(--text-muted)' : 'var(--text-primary)', margin: 0, lineHeight: 1 }}>{stat.value}</p>
                <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', margin: '3px 0 0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.title}</p>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, marginBottom: 10, lineHeight: 1.4, minHeight: '2.8em' }}>{stat.description}</p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: cs.changeColor }}>{stat.change}</span>
                  <ChevronRight size={12} style={{ color: 'var(--text-muted)' }} />
                </div>
                <div style={{ height: 3, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 2, background: barColor, width: `${Math.min(100, (parseInt(stat.value) / Math.max(stat.total || 10, 1)) * 100)}%`, transition: 'width 0.6s ease' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Priority Tasks */}
      <div className="k-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
            <Target size={16} style={{ color: 'var(--gold-500,#c8a03c)' }} />
            Priority Tasks Today
          </h3>
          <span className="k-badge k-badge-pending">{priorityTasks.length} items</span>
        </div>
        <div style={{ padding: '20px' }}>
          {priorityTasks.length === 0 ? (
            <div className="k-empty">
              <CheckCircle size={36} className="k-empty-icon" />
              <p className="k-empty-title">All Caught Up!</p>
              <p className="k-empty-msg">No urgent tasks requiring immediate attention.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(270px,1fr))', gap: '12px' }}>
              {priorityTasks.map((task) => (
                <div
                  key={task.id}
                  onClick={() => onTabChange(task.module)}
                  className={`k-priority-task ${task.priority === 'urgent' ? 'urgent' : task.priority === 'high' ? 'high' : 'medium'}`}
                  title={task.title}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span className={`k-badge ${task.priority === 'urgent' ? 'k-badge-urgent' : task.priority === 'high' ? 'k-badge-high' : 'k-badge-medium'}`}>{task.type}</span>
                    <Timer size={13} style={{ color: 'var(--text-muted)' }} />
                  </div>
                  <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px', lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{task.title}</p>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-muted)' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Clock size={10} /> {task.dueDate}
                    </span>
                    <ChevronRight size={11} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Activity + Insights */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: '20px' }}>
        {/* Recent Activity */}
        <div className="k-card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <Activity size={16} style={{ color: 'var(--forest-400,#4a9e6a)' }} />
              Recent Activity
            </h3>
          </div>
          <div style={{ padding: '16px 20px' }}>
            {activities.length === 0 ? (
              <div className="k-empty" style={{ padding: '24px 0' }}>
                <Activity size={32} className="k-empty-icon" />
                <p className="k-empty-title">No Recent Activity</p>
                <p className="k-empty-msg">Activity will appear here as you work with tasks.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {activities.map((activity) => (
                  <div
                    key={activity.id}
                    onClick={() => onTabChange(activity.module)}
                    className="k-activity-row"
                  >
                    <div className="k-activity-icon-wrap">
                      <activity.icon size={14} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', margin: '0 0 2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{activity.action}</p>
                      <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{activity.details}</p>
                    </div>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>{activity.time}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Module Overview */}
        <div className="k-card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <BarChart3 size={16} style={{ color: 'var(--gold-500,#c8a03c)' }} />
              Module Overview
            </h3>
          </div>
          <div style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {stats.slice(0, 5).map((stat, i) => (
                <div key={i} onClick={stat.onClick} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <stat.icon size={15} style={{ color: statBarColor(stat.color), flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-primary)' }}>{stat.title}</span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)' }}>{stat.value}</span>
                    </div>
                    <div style={{ height: '4px', borderRadius: '2px', background: 'var(--border)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: '2px', background: statBarColor(stat.color), width: `${Math.min(100, (parseInt(stat.value) / Math.max(stat.total || 10, 1)) * 100)}%`, transition: 'width 0.6s ease' }}></div>
                    </div>
                  </div>
                  {getTrendIcon(stat.trend)}
                </div>
              ))}

              {/* Achievement block */}
              <div style={{ marginTop: '8px', padding: '14px', borderRadius: '10px', background: 'linear-gradient(135deg, var(--surface-2), var(--surface-3,var(--surface-2)))', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Award size={22} style={{ color: 'var(--gold-500,#c8a03c)', flexShrink: 0 }} />
                  <div>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 2px' }}>Weekly Goal</p>
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>Keep up the great work!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="k-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
            <Zap size={16} style={{ color: 'var(--gold-500,#c8a03c)' }} />
            Quick Actions — {currentUser.role}
          </h3>
        </div>
        <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: '10px' }}>
          {currentUser.permissions.canViewHT && (
            <button className="k-btn k-btn-ghost" onClick={() => onTabChange('ht-tasks')} style={{ justifyContent: 'flex-start', gap: '8px' }}>
              <Mail size={14} /> View HT Tasks
            </button>
          )}
          {currentUser.permissions.canViewDelegation && (
            <button className="k-btn k-btn-ghost" onClick={() => onTabChange('delegation')} style={{ justifyContent: 'flex-start', gap: '8px' }}>
              <Users size={14} /> View Delegation
            </button>
          )}
          {currentUser.permissions.canViewFMS && (
            <button className="k-btn k-btn-ghost" onClick={() => onTabChange('fms')} style={{ justifyContent: 'flex-start', gap: '8px' }}>
              <FileText size={14} /> FMS Tasks
            </button>
          )}
          {currentUser.permissions.canViewPC && (
            <button className="k-btn k-btn-ghost" onClick={() => onTabChange('pc')} style={{ justifyContent: 'flex-start', gap: '8px' }}>
              <Clipboard size={14} /> PC Dashboard
            </button>
          )}
          {currentUser.permissions.canViewHS && (
            <button className="k-btn k-btn-ghost" onClick={() => onTabChange('hs')} style={{ justifyContent: 'flex-start', gap: '8px' }}>
              <UserPlus size={14} /> Help Slips
            </button>
          )}
          {currentUser.permissions.canViewAnalytics && (
            <button className="k-btn k-btn-ghost" onClick={() => onTabChange('analytics')} style={{ justifyContent: 'flex-start', gap: '8px' }}>
              <BarChart3 size={14} /> Analytics
            </button>
          )}
          {currentUser.permissions.canViewAdmin && (
            <button className="k-btn k-btn-ghost" onClick={() => onTabChange('admin')} style={{ justifyContent: 'flex-start', gap: '8px' }}>
              <Settings size={14} /> Admin Panel
            </button>
          )}
          <button className="k-btn k-btn-outline" onClick={() => onTabChange('notifications')} style={{ justifyContent: 'flex-start', gap: '8px' }}>
            <Bell size={14} /> Notifications
          </button>
          <button className="k-btn k-btn-gold" onClick={() => window.open('https://docs.google.com/forms/d/e/1FAIpQLScAEYqkU-vIFjPJnOAq9STI5d1JNBTtPnPksc0nFg5eSpjaHw/viewform', '_blank')} style={{ justifyContent: 'flex-start', gap: '8px' }}>
            <Mail size={14} /> Create HT
          </button>
          <button className="k-btn k-btn-gold" onClick={() => window.open('https://docs.google.com/forms/d/e/1FAIpQLSdin7lbbC72aF3srwICTyLEfaIQFK4NfER5x_Hxa_cXKKmuJg/viewform', '_blank')} style={{ justifyContent: 'flex-start', gap: '8px' }}>
            <UserPlus size={14} /> Create Help Slip
          </button>
        </div>
      </div>
    </div>
  );
};

export default Overview;
